import BodyHeader from "@app/shared/components/BodyHeader";
import EntitySelect from "@app/shared/components/Entity/EntitySelect";
import { useEntities } from "@app/store/entityStore";
import { useThemes } from "@app/store/themeStore";
import { currentUserAtom } from "@app/store/userStore";
import { Button } from "@design-system/Inputs/Button";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { PageContainer } from "@design-system/Layout/PageContainer";
import { Text } from "@design-system/Typography/Text";
import { UserRole } from "@generated/client/graphql";
import { useMutationState } from "@tanstack/react-query";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { StringParam, useQueryParam } from "use-query-params";
import { CreateObjectiveModal } from "./components/CreateObjectiveModal";
import { selectedThemeIdAtom } from "./components/CreateObjectiveModal/ctx";
import { DittoGuidance } from "./components/DittoGuidance";
import {
    isGuidanceActiveAtom,
    selectedFrameworkAtom,
} from "./components/DittoGuidance/ctx";
import { FilterChangeConfirmationModal } from "./components/FilterChangeConfirmationModal";
import {
    isFilterChangeConfirmationModalOpenAtom,
    pendingFilterChangeAtom,
} from "./components/FilterChangeConfirmationModal/ctx";
import { GenerateObjectiveModal } from "./components/GenerateObjectiveModal";
import { isGenerateObjectiveModalOpenAtom } from "./components/GenerateObjectiveModal/ctx";
import { ObjectiveFilters } from "./components/ObjectiveFilters";
import { ObjectiveProgressOverview } from "./components/ObjectiveProgressOverview";
import { ObjectivesTable } from "./components/ObjectiveTable/ObjectivesTable";
import {
    activeEntityIdAtom,
    isCreateObjectiveModalOpenAtom,
    selectedSubthemesAtomFamily,
    selectedThemesAtomFamily,
} from "./ctx";
import { objectiveKeys, useChooseIndicator, useCreateObjective } from "./data";
import { RESOURCE_NAME, keys } from "./i18n";

export default function Objectives() {
    const [activeEntityId, setActiveEntityId] = useAtom(activeEntityIdAtom);
    const { entities } = useEntities();
    const [urlEntity, setUrlEntity] = useQueryParam("entity", StringParam);

    // Initialize the entity
    useEffect(() => {
        if (
            !!entities?.length &&
            (!activeEntityId ||
                !entities.map((e) => e.id).includes(activeEntityId))
        ) {
            const urlMatchedEntity = entities.find(
                (entity) => entity.name === urlEntity,
            );
            const defaultEntity = entities.find((entity) => !!entity.isDefault);
            setActiveEntityId(
                urlMatchedEntity
                    ? urlMatchedEntity.id
                    : defaultEntity
                      ? defaultEntity.id
                      : entities[0].id,
            );
        }
    }, [entities, setActiveEntityId]);

    // Update the url entity
    useEffect(() => {
        const entity = entities?.find((entity) => entity.id === activeEntityId);
        if (entity) setUrlEntity(entity.name);
    }, [entities, activeEntityId]);

    const { isPending: isCreateObjectivePending } = useCreateObjective();
    const { themes } = useThemes();
    const environmentId = themes?.find(
        (theme) => theme.cmsThemeId === "environment",
    )?.id;
    const setIsCreateObjectiveModalOpen = useSetAtom(
        isCreateObjectiveModalOpenAtom,
    );
    const setCreateObjectiveSelectedThemeId = useSetAtom(selectedThemeIdAtom);

    const { chooseIndicator, isPending: isChooseIndicatorPending } =
        useChooseIndicator();

    const generateObjectiveMutationState = useMutationState({
        filters: { mutationKey: [...objectiveKeys.generateObjective()] },
        select: (mutation) => mutation.state,
    });

    const isGenerateObjectivePending =
        !!generateObjectiveMutationState?.length &&
        generateObjectiveMutationState[
            generateObjectiveMutationState.length - 1
        ].status === "pending";

    const setIsGenerateObjectiveModalOpen = useSetAtom(
        isGenerateObjectiveModalOpenAtom,
    );

    const handleCreateObjectiveClick = () => {
        setIsCreateObjectiveModalOpen(true);
        if (environmentId) {
            setCreateObjectiveSelectedThemeId(environmentId);
        }
    };

    const handleGenerateObjectiveClick = () => {
        if (!activeEntityId) return;
        chooseIndicator(activeEntityId);
        setIsGenerateObjectiveModalOpen(true);
    };

    const { t } = useTranslation(RESOURCE_NAME);

    const { data: currentUser } = useAtomValue(currentUserAtom);
    const isContributor =
        currentUser?.currentUser.permission?.role === UserRole.Contributor;

    // Get the current guidance state
    const [selectedFramework, setSelectedFramework] = useAtom(
        selectedFrameworkAtom,
    );
    const [isGuidanceActive, setIsGuidanceActive] =
        useAtom(isGuidanceActiveAtom);

    // Filter change confirmation modal state
    const [
        isFilterChangeConfirmationModalOpen,
        setIsFilterChangeConfirmationModalOpen,
    ] = useAtom(isFilterChangeConfirmationModalOpenAtom);
    const [pendingFilterChange, setPendingFilterChange] = useAtom(
        pendingFilterChangeAtom,
    );

    // Theme and subtheme state for filter changes
    const { org_uname } = useParams();
    const setSelectedThemesIds = useSetAtom(
        selectedThemesAtomFamily(org_uname),
    );
    const setSelectedSubthemesIds = useSetAtom(
        selectedSubthemesAtomFamily(org_uname),
    );

    // Check if guidance is active and framework is ecovadis
    const shouldShowProgressOverview =
        isGuidanceActive && selectedFramework === "ecovadis";

    const handleFilterChangeConfirm = () => {
        if (!pendingFilterChange) return;

        // Exit guidance view
        setSelectedFramework("");
        setIsGuidanceActive(false);

        // Apply the filter change
        if (pendingFilterChange.type === "theme") {
            setSelectedThemesIds(pendingFilterChange.newValue);
        } else {
            setSelectedSubthemesIds(pendingFilterChange.newValue);
        }

        // Clear pending change and close modal
        setPendingFilterChange(null);
        setIsFilterChangeConfirmationModalOpen(false);
    };

    const handleFilterChangeCancel = () => {
        // Just clear the pending change and close modal, don't apply anything
        setPendingFilterChange(null);
        setIsFilterChangeConfirmationModalOpen(false);
    };

    return (
        <PageContainer>
            <BodyHeader
                dataChameleon={{
                    key: "objectives",
                    type: "tutorial",
                }}
                title={t(keys.page_title)}
                subTitle={t(keys.page_subtitle)}
                action={
                    <>
                        {!isContributor && (
                            <Box>
                                <Button
                                    loading={
                                        isGenerateObjectivePending ||
                                        isChooseIndicatorPending
                                    }
                                    onClick={handleGenerateObjectiveClick}
                                    variant="flatAccent"
                                    disabled={isContributor}
                                >
                                    {t(keys.generate_with_ai)}
                                </Button>
                            </Box>
                        )}

                        <Button
                            size="md"
                            onClick={handleCreateObjectiveClick}
                            loading={isCreateObjectivePending}
                        >
                            <Text variant="body-bold">
                                {t(keys.create_objective)}
                            </Text>
                        </Button>
                    </>
                }
            />
            <FlexCol gap="4" w="full" h="full">
                <DittoGuidance />
                {!!entities?.length && (
                    <FlexRow px="4">
                        <EntitySelect
                            entityId={activeEntityId}
                            setEntityId={setActiveEntityId}
                        />
                    </FlexRow>
                )}
                <ObjectiveFilters />

                {shouldShowProgressOverview && (
                    <ObjectiveProgressOverview entityId={activeEntityId} />
                )}

                <FlexCol w="full" gap="4" px="4">
                    <FlexCol w="full" gap="4">
                        <ObjectivesTable entityId={activeEntityId} />
                    </FlexCol>
                </FlexCol>
            </FlexCol>
            <CreateObjectiveModal />
            <GenerateObjectiveModal />
            <FilterChangeConfirmationModal
                open={isFilterChangeConfirmationModalOpen}
                onOpenChange={setIsFilterChangeConfirmationModalOpen}
                onConfirm={handleFilterChangeConfirm}
                onCancel={handleFilterChangeCancel}
            />
        </PageContainer>
    );
}
